// ============================================
// AlpenScreen AI Settings Component
// File: frontend/src/components/ContentStudio/AISettings.js
// ============================================

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Settings,
  ChevronDown,
  Save,
  RotateCcw,
  Download,
  Trash2,
  Check
} from 'lucide-react';
import { 
  getAIPreferences, 
  saveAIPreferences,
  clearHistory,
  exportHistory,
  clearTranslationMemory,
  INDUSTRIES, // ✅ GEÄNDERT: Jetzt von aiPreferences importiert
  TONES       // ✅ GEÄNDERT: Jetzt von aiPreferences importiert (statt TONE_PRESETS)
} from '../../utils/aiPreferences';

const AISettings = () => {
  const { t } = useTranslation();
  
  const [isOpen, setIsOpen] = useState(false);
  const [preferences, setPreferences] = useState(getAIPreferences());
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Load preferences on mount
  useEffect(() => {
    setPreferences(getAIPreferences());
  }, []);

  const handleSave = () => {
    saveAIPreferences(preferences);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  };

  const handleReset = () => {
    if (window.confirm(t('ai.settings.confirmReset', 'Möchten Sie wirklich alle Einstellungen zurücksetzen?'))) {
      const defaultPrefs = getAIPreferences();
      setPreferences(defaultPrefs);
      saveAIPreferences(defaultPrefs);
    }
  };

  const handleClearHistory = () => {
    if (window.confirm(t('ai.settings.confirmClearHistory', 'Möchten Sie wirklich den gesamten Verlauf löschen?'))) {
      clearHistory();
    }
  };

  const handleExportHistory = () => {
    exportHistory();
  };

  const handleClearTranslationMemory = () => {
    if (window.confirm(t('ai.settings.confirmClearMemory', 'Möchten Sie wirklich den Übersetzungsspeicher löschen?'))) {
      clearTranslationMemory();
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden mb-4 transition-shadow hover:shadow-md">
      {/* Header - Modern & Spacious */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-gradient-to-r hover:from-gray-50 hover:to-slate-50 dark:hover:from-gray-700/30 dark:hover:to-slate-700/30 transition-all duration-200"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-gray-500 to-gray-700 flex items-center justify-center shadow-lg shadow-gray-500/30">
            <Settings size={20} className="text-white" />
          </div>
          <div className="text-left">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {t('ai.settings.title', 'AI Einstellungen')}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
              {t('ai.settings.subtitle', 'Branche, Sprache & Präferenzen')}
            </p>
          </div>
        </div>
        <ChevronDown 
          size={20} 
          className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Content */}
      {isOpen && (
        <div className="border-t border-gray-100 dark:border-gray-700 p-4 space-y-3">
          {/* Industry Selection */}
<div>
  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
    {t('ai.settings.industry', 'Branche')}
  </label>
  <select
    value={preferences.industry}
    onChange={(e) => {
      const newPrefs = { ...preferences, industry: e.target.value };
      setPreferences(newPrefs);
      saveAIPreferences(newPrefs); // ✅ SOFORT speichern!
    }}
    className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
  >
    {INDUSTRIES.map(industry => (
      <option key={industry.id} value={industry.id}>
        {industry.icon} {industry.name}
      </option>
    ))}
  </select>
</div>

{/* Default Tone */}
<div>
  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
    {t('ai.settings.defaultTone', 'Standard-Tonalität')}
  </label>
  <select
    value={preferences.defaultTone}
    onChange={(e) => {
      const newPrefs = { ...preferences, defaultTone: e.target.value };
      setPreferences(newPrefs);
      saveAIPreferences(newPrefs); // ✅ SOFORT speichern!
    }}
    className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
  >
    {TONES.map(tone => (
      <option key={tone.id} value={tone.id}>
        {tone.icon} {tone.name}
      </option>
    ))}
  </select>
  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
    {TONES.find(t => t.id === preferences.defaultTone)?.description}
  </p>
</div>

{/* Language */}
<div>
  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
    {t('ai.settings.language', 'Sprache')}
  </label>
  <select
    value={preferences.language}
    onChange={(e) => {
      const newPrefs = { ...preferences, language: e.target.value };
      setPreferences(newPrefs);
      saveAIPreferences(newPrefs); // ✅ SOFORT speichern!
    }}
    className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
  >
    <option value="de">🇩🇪 Deutsch</option>
    <option value="en">🇬🇧 English</option>
    <option value="fr">🇫🇷 Français</option>
    <option value="it">🇮🇹 Italiano</option>
  </select>
</div>

{/* Batch Count */}
<div>
  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
    {t('ai.settings.batchCount', 'Batch-Generierung')}
  </label>
  <select
    value={preferences.batchCount}
    onChange={(e) => {
      const newPrefs = { ...preferences, batchCount: parseInt(e.target.value) };
      setPreferences(newPrefs);
      saveAIPreferences(newPrefs); // ✅ SOFORT speichern!
    }}
    className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
  >
    <option value="1">1 {t('ai.settings.variant', 'Variante')}</option>
    <option value="2">2 {t('ai.settings.variants', 'Varianten')}</option>
    <option value="3">3 {t('ai.settings.variants', 'Varianten')}</option>
    <option value="5">5 {t('ai.settings.variants', 'Varianten')}</option>
  </select>
  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
    {t('ai.settings.batchHint', 'Anzahl der gleichzeitig generierten Varianten')}
  </p>
</div>

{/* History Size */}
<div>
  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
    {t('ai.settings.historySize', 'Verlauf Größe')}
  </label>
  <select
    value={preferences.maxHistoryItems}
    onChange={(e) => {
      const newPrefs = { ...preferences, maxHistoryItems: parseInt(e.target.value) };
      setPreferences(newPrefs);
      saveAIPreferences(newPrefs); // ✅ SOFORT speichern!
    }}
    className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
  >
    <option value="10">10 {t('ai.settings.items', 'Einträge')}</option>
    <option value="20">20 {t('ai.settings.items', 'Einträge')}</option>
    <option value="50">50 {t('ai.settings.items', 'Einträge')}</option>
    <option value="100">100 {t('ai.settings.items', 'Einträge')}</option>
  </select>
</div>

          {/* Divider */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              {t('ai.settings.dataManagement', 'Datenverwaltung')}
            </p>
            
            <div className="space-y-2">
              {/* Export History */}
              <button
                onClick={handleExportHistory}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 text-blue-700 dark:text-blue-400 rounded-lg hover:from-blue-100 hover:to-cyan-100 dark:hover:from-blue-900/30 dark:hover:to-cyan-900/30 border border-blue-200 dark:border-blue-800 hover:shadow-md transition-all"
              >
                <Download size={16} />
                {t('ai.settings.exportHistory', 'Verlauf exportieren')}
              </button>

              {/* Clear History */}
              <button
                onClick={handleClearHistory}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 text-orange-700 dark:text-orange-400 rounded-lg hover:from-orange-100 hover:to-red-100 dark:hover:from-orange-900/30 dark:hover:to-red-900/30 border border-orange-200 dark:border-orange-800 hover:shadow-md transition-all"
              >
                <Trash2 size={16} />
                {t('ai.settings.clearHistory', 'Verlauf löschen')}
              </button>

              {/* Clear Translation Memory */}
              <button
                onClick={handleClearTranslationMemory}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 text-orange-700 dark:text-orange-400 rounded-lg hover:from-orange-100 hover:to-red-100 dark:hover:from-orange-900/30 dark:hover:to-red-900/30 border border-orange-200 dark:border-orange-800 hover:shadow-md transition-all"
              >
                <Trash2 size={16} />
                {t('ai.settings.clearTranslationMemory', 'Übersetzungsspeicher löschen')}
              </button>
            </div>
          </div>

          {/* Save/Reset Buttons */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleReset}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 hover:shadow-md transition-all"
            >
              <RotateCcw size={16} />
              {t('ai.settings.reset', 'Zurücksetzen')}
            </button>
            
            <button
              onClick={handleSave}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold rounded-lg transition-all hover:shadow-lg ${
                saveSuccess
                  ? 'bg-green-600 text-white'
                  : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 hover:shadow-purple-500/30'
              }`}
            >
              {saveSuccess ? <Check size={16} /> : <Save size={16} />}
              {saveSuccess 
                ? t('ai.settings.saved', 'Gespeichert!') 
                : t('ai.settings.save', 'Speichern')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AISettings;